import { ChevronRightIcon } from '@heroicons/react/24/outline';
import type { ColumnType } from 'antd/es/table';

import { sortByAmount, sortByTime } from 'utils/antd/sorters';
import { formatDecimalAmount } from 'utils/formatters/formatCurrency';

import type { PayoutDraftsItem } from 'api';

import type { FindAccountByAccountNumber } from 'hooks/context/useVirtualAccounts';

import { ColumnTitle } from '@atoms/ColumnTitle';
import { PaperClipIcon } from '@atoms/Icon/PaperClipIcon';
import { StatusTag } from '@atoms/StatusTag';

import { FormattedDateTime } from '@molecules/FormattedDateTime';

import { PAYOUT_DRAFTS_STATUSES } from './constants';

export const getColumns = (
  findAccountByAccountNumber: FindAccountByAccountNumber
): ColumnType<PayoutDraftsItem>[] => {
  return [
    {
      key: 'empty_space',
      width: 50
    },
    {
      title: <ColumnTitle title="Date" />,
      dataIndex: 'created_at',
      key: 'created_at',
      render: (createdAt: string) => <FormattedDateTime value={createdAt} />,
      sorter: (a, b) => sortByTime(a.created_at, b.created_at),
      onHeaderCell: () => ({
        className: 'align-sorter-icon'
      })
    },
    {
      title: <ColumnTitle title="Created by" />,
      dataIndex: 'creator_name',
      key: 'creator_name',
      render: (creatorName: string) => (
        <div className="font-semibold">{creatorName}</div>
      )
    },
    {
      title: <ColumnTitle title="ID" />,
      dataIndex: 'end_to_end_id',
      key: 'end_to_end_id',
      render: (endToEndId: string) => (
        <div className="font-semibold">{endToEndId}</div>
      ),
      onHeaderCell: () => ({
        className: 'align-sorter-icon'
      })
    },
    {
      title: <ColumnTitle title="Corridor" />,
      dataIndex: 'corridor',
      key: 'corridor',
      align: 'right',
      render: (corridor: string, record: PayoutDraftsItem) => {
        return (
          <div>
            {record.local_instrument}
            <div className="font-semibold">{corridor}</div>
          </div>
        );
      }
    },
    {
      title: <ColumnTitle title="Account" />,
      dataIndex: 'debtor_account',
      key: 'debtor_account',
      align: 'right',
      render: (debtorAccountNumber) => {
        const account = findAccountByAccountNumber(debtorAccountNumber);
        return (
          <div>
            {debtorAccountNumber}
            <div className="font-semibold">{account?.name}</div>
          </div>
        );
      }
    },
    {
      title: <ColumnTitle title="Amount" />,
      dataIndex: 'amount',
      key: 'amount',
      align: 'right',
      sorter: (a, b) => sortByAmount(a.amount, b.amount),
      render: (amount, record: PayoutDraftsItem) => {
        const formattedAmount = formatDecimalAmount(amount);
        return (
          <div>
            {record.currency}
            <div className="font-semibold">{formattedAmount}</div>
          </div>
        );
      }
    },
    {
      title: <ColumnTitle title="Status" />,
      dataIndex: 'status',
      key: 'status',
      align: 'right',
      render: (status: string) => (
        <StatusTag status={status} statuses={PAYOUT_DRAFTS_STATUSES} />
      )
    },
    {
      dataIndex: 'attachments',
      key: 'paperClipIcon',
      align: 'right',
      width: 30,
      render: (_: unknown, record: PayoutDraftsItem) => {
        if (record.attachments_count > 0) {
          return <PaperClipIcon className="h-9 stroke-content-subtle pt-3" />;
        }
        return null;
      }
    },
    {
      dataIndex: 'icon',
      key: 'arrowIcon',
      align: 'right',
      width: 50,
      render: () => (
        <ChevronRightIcon className="h-9 stroke-content-subtle pt-3" />
      )
    }
  ];
};
